import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useOpensUp } from '../common/useOpensUp';
import { OrganizationHierarchy } from '../../types/organization';
import { organizationService } from '../../services/organizationService';
import { useTaxonomyColors } from '../../hooks/useTaxonomyColors';
import { controlClass, Spinner, Swatch } from '../ui';

import { foldText } from '../../utils/fuzzySearch';
interface OrganizationTreeSelectProps {
  selectedId?: string;
  onSelect: (organizationId: string | null) => void;
  excludeId?: string;
  placeholder?: string;
}

/** "religious-organization" → "Religious organization" */
const typeLabel = (type?: string) => {
  if (!type) return '';
  // Only reformat slugs; labels such as "Non-Governmental Organization (NGO)" stay as they are
  if (!/^[a-z0-9]+([-_][a-z0-9]+)*$/.test(type)) return type;
  const text = type.replace(/[-_]+/g, ' ');
  // Short single words are acronyms: "ngo" → "NGO"
  if (!text.includes(' ') && text.length <= 4) return text.toUpperCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/** Show the filter field once the tree is long enough to need it */
const SEARCH_THRESHOLD = 8;

const countNodes = (nodes: OrganizationHierarchy[]): number =>
  nodes.reduce((sum, node) => sum + 1 + countNodes(node.children || []), 0);

const matchesQuery = (node: OrganizationHierarchy, query: string) =>
  foldText(node.name).includes(query) || foldText(node.type).includes(query);

/** Keep nodes that match, plus the ancestors that lead to them */
const filterTree = (nodes: OrganizationHierarchy[], query: string): OrganizationHierarchy[] =>
  nodes.reduce<OrganizationHierarchy[]>((acc, node) => {
    const children = filterTree(node.children || [], query);
    if (matchesQuery(node, query) || children.length) acc.push({ ...node, children });
    return acc;
  }, []);

/** First node in display order that matches the query itself (not just an ancestor of a match) */
const findFirstMatch = (nodes: OrganizationHierarchy[], query: string): OrganizationHierarchy | null => {
  for (const node of nodes) {
    if (matchesQuery(node, query)) return node;
    const match = findFirstMatch(node.children || [], query);
    if (match) return match;
  }
  return null;
};

export const OrganizationTreeSelect: React.FC<OrganizationTreeSelectProps> = ({
  selectedId,
  onSelect,
  excludeId,
  placeholder = "Select parent organization"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationHierarchy[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationHierarchy | null>(null);
  const [query, setQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const opensUp = useOpensUp(isOpen, triggerRef, 320);
  const { colorOf } = useTaxonomyColors();

  const filterExcludedOrg = useCallback((orgs: OrganizationHierarchy[]): OrganizationHierarchy[] => {
    if (!excludeId) return orgs;

    return orgs
      .filter(org => org.id !== excludeId)
      .map(org => ({
        ...org,
        children: filterExcludedOrg(org.children)
      }));
  }, [excludeId]);

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await organizationService.getOrganizationHierarchy();
      setOrganizations(filterExcludedOrg(data));
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  }, [filterExcludedOrg]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    if (selectedId && organizations.length > 0) {
      const findOrgById = (orgs: OrganizationHierarchy[]): OrganizationHierarchy | null => {
        for (const org of orgs) {
          if (org.id === selectedId) return org;
          const found = findOrgById(org.children);
          if (found) return found;
        }
        return null;
      };
      setSelectedOrg(findOrgById(organizations));
    } else {
      setSelectedOrg(null);
    }
  }, [selectedId, organizations]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showSearch = countNodes(organizations) > SEARCH_THRESHOLD;

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      return;
    }
    if (showSearch) window.setTimeout(() => searchRef.current?.focus(), 0);
  }, [isOpen, showSearch]);

  const visibleOrganizations = useMemo(() => {
    const q = foldText(query);
    return q ? filterTree(organizations, q) : organizations;
  }, [organizations, query]);

  const handleSelect = (org: OrganizationHierarchy | null) => {
    setSelectedOrg(org);
    onSelect(org?.id || null);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const onPanelKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setIsOpen(false);
      triggerRef.current?.focus();
      return;
    }
    const options = Array.from(dropdownRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? []);
    if (event.key === 'Enter') {
      // Never submit the surrounding form; pick the focused option, or the first match from the search field
      const target = event.target as HTMLElement;
      if (target === searchRef.current) {
        event.preventDefault();
        const q = foldText(query);
        const match = q ? findFirstMatch(organizations, q) : null;
        if (match) handleSelect(match);
      } else if (target.getAttribute('role') === 'option') {
        event.preventDefault();
        target.click();
      }
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (!options.length) return;
    event.preventDefault();
    const index = options.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'ArrowDown' ? Math.min(index + 1, options.length - 1) : Math.max(index - 1, 0);
    options[index === -1 ? 0 : next].focus();
  };

  const optionClass = (selected: boolean) =>
    `flex h-9 w-full items-center gap-2 rounded-xl pr-2.5 text-left text-[13px] transition-colors hover:bg-neutral-100 focus-visible:bg-neutral-100 focus-visible:outline-none ${
      selected ? 'text-ink' : 'text-neutral-700'
    }`;

  /** `trail[i]` is true when the ancestor at depth i + 1 has siblings below it, so its line continues */
  const renderOrganizationTree = (orgs: OrganizationHierarchy[], level = 0, trail: boolean[] = []) => {
    return orgs.map((org, index) => {
      const selected = selectedId === org.id;
      const isLast = index === orgs.length - 1;
      const lineLeft = (depth: number) => 12 + (depth - 1) * 20 + 4;
      return (
        <div key={org.id}>
          <button
            type="button"
            role="option"
            aria-selected={selected}
            onClick={() => handleSelect(org)}
            className={`relative ${optionClass(selected)}`}
            style={{ paddingLeft: `${12 + level * 20}px` }}
          >
            {level > 0 && (
              <span aria-hidden="true">
                {trail.map((continues, depth) =>
                  continues ? (
                    <span
                      key={depth}
                      className="absolute inset-y-0 w-px bg-neutral-200"
                      style={{ left: lineLeft(depth + 1) }}
                    />
                  ) : null,
                )}
                <span
                  className={`absolute top-0 w-px bg-neutral-200 ${isLast ? 'h-1/2' : 'h-full'}`}
                  style={{ left: lineLeft(level) }}
                />
                <span className="absolute top-1/2 h-px w-2 bg-neutral-200" style={{ left: lineLeft(level) }} />
              </span>
            )}
            <span className="min-w-0 flex-1 truncate">{org.name}</span>
            {org.type && (
              <span className="flex max-w-[40%] flex-shrink-0 items-center gap-1.5 text-[11px] text-neutral-400">
                {colorOf('organization_type', org.type) && (
                  <Swatch color={colorOf('organization_type', org.type)} />
                )}
                <span className="truncate">{typeLabel(org.type)}</span>
              </span>
            )}
            <Check
              className={`h-4 w-4 flex-shrink-0 text-primary-600 ${selected ? '' : 'invisible'}`}
              strokeWidth={2}
              aria-hidden="true"
            />
          </button>
          {org.children.length > 0 &&
            renderOrganizationTree(org.children, level + 1, level > 0 ? [...trail, !isLast] : [])}
        </div>
      );
    });
  };

  return (
    <div className="relative font-ui" ref={dropdownRef} onKeyDown={isOpen ? onPanelKeyDown : undefined}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`${controlClass} relative flex h-10 items-center pl-3 pr-9 text-left sm:h-9`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={`block truncate ${selectedOrg ? '' : 'text-neutral-400'}`}>
          {selectedOrg ? selectedOrg.name : placeholder}
        </span>
        <ChevronDown
          className={`pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          strokeWidth={1.75}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div className={`absolute left-0 right-0 z-30 ${opensUp ? 'bottom-full mb-1.5' : 'mt-1.5'} min-w-[240px] animate-fade-up rounded-2xl bg-white p-1.5 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.2)] ring-1 ring-black/[0.06]`}>
          {showSearch && !loading && (
            <div className="relative mb-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                strokeWidth={1.75}
              />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                aria-label="Search organizations"
                className="block h-9 w-full rounded-xl border-0 bg-neutral-100 pl-9 pr-3 text-[16px] text-ink placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-600/25 sm:text-[13px]"
              />
            </div>
          )}
          <div role="listbox" aria-label="Organizations" className="max-h-72 overflow-y-auto overscroll-contain">
            {loading ? (
              <div className="flex h-9 items-center gap-2 px-3 text-[13px] text-neutral-400">
                <Spinner />
                Loading organizations...
              </div>
            ) : (
              <>
                {!query && (
                  <button
                    type="button"
                    role="option"
                    aria-selected={!selectedId}
                    onClick={() => handleSelect(null)}
                    className={`${optionClass(!selectedId)} pl-3`}
                  >
                    <span className="min-w-0 flex-1 truncate text-neutral-500">No parent organization</span>
                    <Check
                      className={`h-4 w-4 flex-shrink-0 text-primary-600 ${selectedId ? 'invisible' : ''}`}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </button>
                )}
                {organizations.length === 0 ? (
                  <div className="flex h-9 items-center px-3 text-[13px] text-neutral-400">No organizations available</div>
                ) : visibleOrganizations.length === 0 ? (
                  <div className="flex h-9 items-center px-3 text-[13px] text-neutral-400">No matches</div>
                ) : (
                  renderOrganizationTree(visibleOrganizations)
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
